/**
 * Patient Welcome Component
 * Final onboarding step with welcome message and dashboard access
 */
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Celebration,
  Dashboard,
  CalendarToday,
  Message,
  Description,
  Support,
  CheckCircle
} from '@mui/icons-material';

const PatientWelcome = ({ onboardingState, onGoToDashboard }) => {
  const { trial } = onboardingState || {};

  const dashboardFeatures = [
    {
      icon: CalendarToday,
      title: 'Book Appointments',
      description: 'Schedule consultations with healthcare providers'
    },
    {
      icon: Message,
      title: 'Secure Messaging',
      description: 'Communicate securely with your healthcare team'
    },
    {
      icon: Description,
      title: 'Medical Records',
      description: 'Access and manage your health information'
    },
    {
      icon: Support,
      title: '24/7 Support',
      description: 'Get help whenever you need it'
    }
  ];

  return (
    <Box p={3} maxWidth={800} mx="auto">
      <Card>
        <CardContent>
          {/* Celebration Header */}
          <Box textAlign="center" mb={4}>
            <Celebration sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" gutterBottom>
              Welcome to MindWell! 🎉
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your account is ready and you're all set to begin your healthcare journey
            </Typography>
          </Box>

          {/* Trial Information */}
          {trial?.is_active && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Free Trial Active!</strong>
              </Typography>
              <Typography variant="body2">
                You have {trial.days_remaining} days of free access to all MindWell features. 
                Explore everything we have to offer!
              </Typography>
            </Alert>
          )}

          {/* What's Next Section */}
          <Box mb={4}>
            <Typography variant="h5" gutterBottom>
              What's available in your dashboard:
            </Typography>
            
            <List>
              {dashboardFeatures.map((feature, index) => (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemIcon>
                    <feature.icon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature.title}
                    secondary={feature.description}
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Profile Completion Status */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Profile Status
            </Typography>
            
            <Alert severity="success" icon={<CheckCircle />}>
              <Typography variant="body2">
                Great job! Your basic profile is complete. You can add more details 
                anytime from your dashboard settings.
              </Typography>
            </Alert>
          </Box>

          {/* Next Steps */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Recommended Next Steps
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      📋 Complete Your Medical History
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Help providers give you better care by sharing your medical background
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      📄 Upload Medical Documents
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload existing reports, prescriptions, and medical records
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Action Button */}
          <Box textAlign="center" mb={3}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Dashboard />}
              onClick={onGoToDashboard}
              sx={{ px: 4, py: 1.5 }}
            >
              Go to My Dashboard
            </Button>
          </Box>

          {/* Support Information */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Need help getting started?</strong><br />
              Our support team is here to help you make the most of MindWell. 
              You can find help and contact options in your dashboard.
            </Typography>
          </Alert>

          {/* Footer Note */}
          <Box mt={3} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Thank you for choosing MindWell for your healthcare needs. 
              We're excited to be part of your wellness journey! 💙
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PatientWelcome;
